/**
 * 新規予約・変更申請共通の利用日選択用カスタムカレンダーUI
 * (Phase 11 Part D/V「予約済み日カレンダー不可表示」、Phase 11続行分
 * 「祝日をカレンダーUIでも正しく反映」)。
 *
 * `<input type="date">`ではiOSのネイティブ日付ホイールが「日付ごとの
 * 個別disabled・予約不可・祝日等の視覚的区別」を表現できないため、この
 * 用途に限り最小限の自前カレンダーへ置き換える(大型UIライブラリは使わない)。
 *
 * このUIはあくまで事前案内であり、最終的な予約可否判定・料金・祝日確定は
 * サーバー側(POST /v1/bookings の occupancy再判定・Rule Engine・
 * isHoliday())を正とする。祝日データ自体もこのモジュールでは一切推測・
 * 計算せず、呼び出し側がGET /v1/availability/datesのholiday_dates
 * (backend側のGoogle Calendar「日本の祝日」判定結果)をそのまま渡す
 * (holidayDatesオプション)。
 *
 * 色分けの優先順位(視認性の競合を避けるため、1セルにつき「操作可否」を
 * 表す状態を最優先で表示し、日種別の色はそれより弱い付加情報として扱う):
 * 選択中(cal-selected) > 予約不可(cal-occupied) > 範囲外(cal-out-of-range)
 * > 祝日(cal-day-holiday) > 土曜(cal-day-saturday) > 日曜(cal-day-sunday)
 * > 平日(既定)。範囲外・予約不可セルには祝日/曜日の色を重ねない(disabled
 * である事実の視認性を優先する)。
 */

const WEEKDAY_LABELS_JA = ["日", "月", "火", "水", "木", "金", "土"] as const;

export interface CalendarWidgetOptions {
  /** 表示する年月(1-12)。 */
  year: number;
  month: number;
  /** 予約可能範囲(両端含む、"YYYY-MM-DD")。 */
  minDate: string;
  maxDate: string;
  /** 現在選択中の利用日("" なら未選択)。 */
  selectedDate: string;
  /** 予約済み(予約不可)の利用日集合。 */
  unavailableDates: ReadonlySet<string>;
  /**
   * 祝日の利用日集合(GET /v1/availability/datesのholiday_dates、
   * backend側Google Calendar「日本の祝日」判定結果をそのまま使う)。
   * 未指定時は空集合として扱う(土日のみの色分けになる)。
   */
  holidayDates?: ReadonlySet<string>;
  /**
   * Google Calendarの「予約不可」を確認できなかった場合true。予約可能か判断
   * できないため、全ての日付を選択不可にして理由を表示する。
   */
  calendarCheckFailed?: boolean;
  onSelectDate: (dateIso: string) => void;
  onMonthChange: (year: number, month: number) => void;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function isoOf(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${pad2(month)}-${pad2(day)}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function firstWeekdayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
}

function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const zeroBased = (month - 1) + delta;
  const targetYear = year + Math.floor(zeroBased / 12);
  const targetMonth0 = ((zeroBased % 12) + 12) % 12;
  return { year: targetYear, month: targetMonth0 + 1 };
}

type DayKind = "holiday" | "saturday" | "sunday" | "weekday";

function dayKindOf(year: number, month: number, day: number, holidayDates: ReadonlySet<string>, iso: string): DayKind {
  if (holidayDates.has(iso)) return "holiday";
  const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  if (dow === 6) return "saturday";
  if (dow === 0) return "sunday";
  return "weekday";
}

const DAY_KIND_CLASS: Record<DayKind, string> = {
  holiday: "cal-day-holiday",
  saturday: "cal-day-saturday",
  sunday: "cal-day-sunday",
  weekday: "",
};

const DAY_KIND_LABEL: Record<DayKind, string> = {
  holiday: "祝日",
  saturday: "土曜",
  sunday: "日曜",
  weekday: "",
};

export function renderCalendarWidget(container: HTMLElement, options: CalendarWidgetOptions): void {
  const { year, month, minDate, maxDate, selectedDate, unavailableDates } = options;
  const holidayDates = options.holidayDates ?? new Set<string>();
  const calendarCheckFailed = options.calendarCheckFailed === true;

  const prevMonth = addMonths(year, month, -1);
  const nextMonth = addMonths(year, month, 1);
  const prevMonthLastDayIso = isoOf(prevMonth.year, prevMonth.month, daysInMonth(prevMonth.year, prevMonth.month));
  const nextMonthFirstDayIso = isoOf(nextMonth.year, nextMonth.month, 1);
  const prevDisabled = prevMonthLastDayIso < minDate;
  const nextDisabled = nextMonthFirstDayIso > maxDate;

  const totalDays = daysInMonth(year, month);
  const leadingBlanks = firstWeekdayOfMonth(year, month);

  const dayCells: string[] = [];
  for (let i = 0; i < leadingBlanks; i++) {
    dayCells.push(`<div class="cal-cell cal-blank" aria-hidden="true"></div>`);
  }
  for (let day = 1; day <= totalDays; day++) {
    const iso = isoOf(year, month, day);
    const isOccupied = unavailableDates.has(iso);
    const inWindow = iso >= minDate && iso <= maxDate;
    const isSelected = iso === selectedDate;
    const kind = dayKindOf(year, month, day, holidayDates, iso);
    const kindLabel = DAY_KIND_LABEL[kind];

    if (!inWindow) {
      dayCells.push(
        `<div class="cal-cell"><button type="button" class="cal-day cal-out-of-range" disabled aria-label="${day}日は選択できません">${day}</button></div>`,
      );
    } else if (calendarCheckFailed && !isOccupied) {
      // 空き状況を確認できない日は「予約可能」と見せない(選択不可)。
      dayCells.push(
        `<div class="cal-cell"><button type="button" class="cal-day cal-out-of-range" disabled data-date="${iso}" aria-label="${day}日は空き状況を確認できないため選択できません">${day}</button></div>`,
      );
    } else if (isOccupied) {
      dayCells.push(
        `<div class="cal-cell"><button type="button" class="cal-day cal-occupied" disabled data-date="${iso}" aria-label="${day}日は予約不可">${day}<span class="cal-occupied-mark" aria-hidden="true">×</span></button></div>`,
      );
    } else {
      const kindClass = DAY_KIND_CLASS[kind];
      const label = kindLabel ? `${day}日(${kindLabel})を選択` : `${day}日を選択`;
      const title = kindLabel ? `${kindLabel}` : "";
      dayCells.push(
        `<div class="cal-cell"><button type="button" class="cal-day${kindClass ? ` ${kindClass}` : ""}${isSelected ? " cal-selected" : ""}" data-date="${iso}" aria-pressed="${isSelected}" aria-label="${label}"${title ? ` title="${title}"` : ""}>${day}</button></div>`,
      );
    }
  }

  container.innerHTML = `
    <div class="cal-header">
      <button type="button" class="cal-nav" data-action="cal-prev" ${prevDisabled ? "disabled" : ""} aria-label="前の月">‹</button>
      <span class="cal-title">${year}年${month}月</span>
      <button type="button" class="cal-nav" data-action="cal-next" ${nextDisabled ? "disabled" : ""} aria-label="次の月">›</button>
    </div>
    <div class="cal-weekdays">
      ${WEEKDAY_LABELS_JA.map((w) => `<div class="cal-weekday">${w}</div>`).join("")}
    </div>
    ${calendarCheckFailed ? `<p class="cal-error" role="alert" style="color:#c42a1c;font-size:13px;margin:6px 0;">空き状況を確認できませんでした。時間をおいて、もう一度お試しください。(日付は選択できません)</p>` : ""}
    <div class="cal-grid">
      ${dayCells.join("")}
    </div>
    <p class="cal-legend">
      <span class="cal-legend-item"><span class="cal-legend-swatch cal-legend-swatch-saturday" aria-hidden="true"></span>土曜</span>
      <span class="cal-legend-item"><span class="cal-legend-swatch cal-legend-swatch-sunday" aria-hidden="true"></span>日曜</span>
      <span class="cal-legend-item"><span class="cal-legend-swatch cal-legend-swatch-holiday" aria-hidden="true"></span>祝日</span>
      <span class="cal-legend-item"><span class="cal-legend-swatch cal-legend-occupied">×</span>予約不可</span>
    </p>
  `;

  container.querySelector('[data-action="cal-prev"]')?.addEventListener("click", () => {
    if (prevDisabled) return;
    options.onMonthChange(prevMonth.year, prevMonth.month);
  });
  container.querySelector('[data-action="cal-next"]')?.addEventListener("click", () => {
    if (nextDisabled) return;
    options.onMonthChange(nextMonth.year, nextMonth.month);
  });
  container.querySelectorAll<HTMLButtonElement>(".cal-day[data-date]:not(:disabled)").forEach((btn) => {
    btn.addEventListener("click", () => {
      const iso = btn.dataset.date;
      if (iso) options.onSelectDate(iso);
    });
  });
}
